import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Channel } from '../utils';
import {
  sendProStudioEvent,
  useIsProStudioEventAllowed,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { useProStudioViewerRole } from './use-pro-studio-viewer-role';

/**
 * Per-channel Analytics event logging (the "Connected" / "Not Connected"
 * control). Shared by the Channels page and the Design channel panel so both
 * drive the same `analyticsToggle` event with the same optimistic state.
 *
 * `hasAnalytics` is false when the project has no Analytics tool, in which
 * case callers render nothing.
 */
export const useChannelAnalyticsToggle = (channels: Channel[]) => {
  const isViewer = useProStudioViewerRole();
  const isAnalyticsEventAllowed = useIsProStudioEventAllowed('analyticsToggle');
  const hasAnalytics = useProStudioCtxSelector(
    (ctx) =>
      ctx.projectTools?.some((tool) => tool.name === 'ANALYTICS') ?? false
  );
  const [analyticsState, setAnalyticsState] = useState<Map<string, boolean>>(
    new Map()
  );
  const [pendingChannels, setPendingChannels] = useState<Set<string>>(
    new Set()
  );

  // Fresh channel data from the server replaces any optimistic state.
  const prevChannelsRef = useRef(channels);
  useEffect(() => {
    if (prevChannelsRef.current !== channels) {
      prevChannelsRef.current = channels;
      setAnalyticsState(new Map());
      setPendingChannels(new Set());
    }
  }, [channels]);

  const isConnected = useCallback(
    (channel: Channel) =>
      analyticsState.has(channel.streamId)
        ? analyticsState.get(channel.streamId)!
        : (channel.analyticsEnabled ?? false),
    [analyticsState]
  );

  const isPending = useCallback(
    (channel: Channel) => pendingChannels.has(channel.streamId),
    [pendingChannels]
  );

  const isInteractive = useCallback(
    (channel: Channel) =>
      !isViewer &&
      !pendingChannels.has(channel.streamId) &&
      isAnalyticsEventAllowed,
    [isViewer, pendingChannels, isAnalyticsEventAllowed]
  );

  const toggle = useCallback(
    (channel: Channel) => {
      if (!isInteractive(channel)) return;
      const { id: channelId, streamId } = channel;
      const wasConnected = isConnected(channel);
      const newValue = !wasConnected;
      setAnalyticsState((prev) => new Map(prev).set(streamId, newValue));
      setPendingChannels((prev) => new Set(prev).add(streamId));

      const clearPending = () => {
        setPendingChannels((prev) => {
          const next = new Set(prev);
          next.delete(streamId);
          return next;
        });
      };

      sendProStudioEvent({
        type: 'analyticsToggle',
        payload: {
          channelId,
          streamId,
          env: channel.env,
          enabled: newValue,
          onDone: clearPending,
          onError: () => {
            setAnalyticsState((prev) =>
              new Map(prev).set(streamId, wasConnected)
            );
            clearPending();
          },
        },
      });
    },
    [isInteractive, isConnected]
  );

  // Stable between renders unless the state changes, so callers can list it
  // as a memo dependency.
  return useMemo(
    () => ({ hasAnalytics, isConnected, isPending, isInteractive, toggle }),
    [hasAnalytics, isConnected, isPending, isInteractive, toggle]
  );
};
